import { apiFetch, type RequestOptions } from './client'
import { relatedResource } from './documents'
import type { Document, OrganizationAttributes, Resource } from './types'
import { toUser, type User } from './users'

/**
 * The family, as the API models it: an organization classified as a household,
 * which the parent owns and each child belongs to as a student.
 */
export interface Household {
  id: string
  name: string
}

/**
 * The household a parent owns, or null for someone who owns none.
 *
 * Found through their ownership memberships, which come back with each
 * organization included. A parent can own more than one organization (a guide
 * organization, say), so the household is picked by classification, never by
 * position.
 */
export async function fetchHousehold(
  userId: string,
  options: RequestOptions = {},
): Promise<Household | null> {
  const query = new URLSearchParams({ 'filter[role]': 'ownership' })
  const document = await apiFetch<Document<Resource[]>>(
    `/users/${userId}/memberships?${query}`,
    options,
  )

  for (const membership of document.data) {
    const organization = relatedResource(document, membership, 'organization')
    const attributes = organization?.attributes as OrganizationAttributes | undefined
    if (organization && attributes?.classification === 'household') {
      return { id: organization.id, name: attributes.name }
    }
  }

  return null
}

/**
 * The household doubles as the organization context its own endpoints
 * require, so it is added to the auth headers here rather than by every caller.
 */
export function withinHousehold(household: Household, options: RequestOptions): RequestOptions {
  return options.auth
    ? { ...options, auth: { ...options.auth, organizationId: household.id } }
    : options
}

/** The children in a household. */
export async function fetchChildren(
  household: Household,
  options: RequestOptions = {},
): Promise<User[]> {
  const query = new URLSearchParams({ 'filter[scope]': 'child' })
  const document = await apiFetch<Document<Resource[]>>(
    `/organizations/${household.id}/users?${query}`,
    withinHousehold(household, options),
  )

  return document.data.map(toUser)
}
